/**
 * Lodgement buffer and the lock on a campaign that has been paid for or posted.
 *
 * The page and the API both call these, so a bypassed click still fails.
 * A date inside the buffer is never written. A customer cannot change or
 * cancel a committed campaign; staff can, after the page has asked them.
 */

import { lodgementDayBlocker } from "./lodgement-days.ts";
import { allowedSpeeds, type MailType, productAllows } from "./mail-rules.ts";

export const LODGEMENT_BUFFER_DAYS = 5;
export const SUPPORT_PHONE = "1300 767 843";

export const CANNOT_CHANGE_MESSAGE = `This cannot be changed. Please call eConnect Plus on ${SUPPORT_PHONE}.`;
export const CANNOT_CANCEL_MESSAGE = `This campaign cannot be cancelled. Please call eConnect Plus on ${SUPPORT_PHONE}.`;
export const CANNOT_DELETE_MESSAGE = `This campaign cannot be deleted. Please call eConnect Plus on ${SUPPORT_PHONE}.`;

/** Strictly more than five days out. A date on the boundary is too close. */
export function lodgementAcceptable(epoch: number | null, nowMs = Date.now()): boolean {
  if (epoch === null) return false;
  return epoch * 1000 > nowMs + LODGEMENT_BUFFER_DAYS * 86_400_000;
}

/** Past the buffer and a day Australia Post takes this mail type. */
export function lodgementOpen(epoch: number | null, mailType: string, nowMs = Date.now()): boolean {
  return (
    lodgementAcceptable(epoch, nowMs) && lodgementDayBlocker(epoch as number, mailType) === null
  );
}

/** The first open lodgement day, as UTC midnight epoch seconds. */
export function nextLodgementDay(mailType: string, nowMs = Date.now()): number {
  let day = Math.floor(nowMs / 86_400_000) * 86_400;
  // Unaddressed skips at most the December blackout, so a year always finds one.
  for (let i = 0; i < 400 && !lodgementOpen(day, mailType, nowMs); i++) day += 86_400;
  return day;
}

export function lodgementWarning(formattedDate: string | null): string {
  if (!formattedDate) return "Please select a Lodgement date to continue.";
  return `The lodgement date of ${formattedDate} is too close to the current date. Please contact eConnect Plus on ${SUPPORT_PHONE} to check that we can produce your campaign in time.`;
}

/**
 * The artwork change is not applied. Paid, in production, posted, or a
 * lodgement date inside the five-day cutoff. A campaign with no date is not
 * inside the cutoff.
 */
export function artworkChangeHeld(
  campaign: {
    status: string;
    order_status?: string | null;
    lodgement_date?: number | null;
  },
  nowMs = Date.now(),
): boolean {
  if (customerEditBlocked(campaign.status, campaign.order_status)) return true;
  if (campaign.lodgement_date == null) return false;
  return !lodgementAcceptable(campaign.lodgement_date, nowMs);
}

/** Shown when a held change is refused, and as the customer's stop dialog. */
export function artworkHeldNotice(
  campaign: {
    status: string;
    order_status?: string | null;
    lodgement_date?: number | null;
  },
  nowMs = Date.now(),
): string | null {
  if (!artworkChangeHeld(campaign, nowMs)) return null;
  if (customerEditBlocked(campaign.status, campaign.order_status)) {
    return "Your campaign is in production. You can cancel, or email eConnect and ask them to stop.";
  }
  return "The lodgement date is inside the 5-day cutoff. You can cancel, or email eConnect and ask them to stop.";
}

export const ARTWORK_HELD_MESSAGE =
  "This change was not applied. The campaign is in production or inside the lodgement cutoff.";

/** Paid or in production. Posted is finished even when the order row says something else. */
export function customerEditBlocked(
  campaignStatus: string,
  orderStatus: string | null | undefined,
): boolean {
  return campaignStatus === "posted" || orderStatus === "paid" || orderStatus === "in_production";
}

export function orderStatusLabel(status: string): string {
  if (status === "in_production") return "in production";
  return status.replaceAll("_", " ");
}

/**
 * Why a campaign cannot go in the cart yet. Empty means it can be priced.
 * Artwork and approval carry on in parallel; they are not cart blockers.
 */
export function cartBlockers(
  campaign: {
    product_id: string | null;
    address_count: number;
    lodgement_date: number | null;
    mail_type: string;
  },
  nowMs = Date.now(),
): string[] {
  const out: string[] = [];
  if (!campaign.product_id) out.push("Choose a product.");
  if (campaign.address_count <= 0) out.push("Select an area or upload an address list.");
  if (!lodgementAcceptable(campaign.lodgement_date, nowMs)) {
    out.push(
      campaign.lodgement_date
        ? `Choose a lodgement date more than ${LODGEMENT_BUFFER_DAYS} days away.`
        : "Set a lodgement date.",
    );
  } else {
    const day = lodgementDayBlocker(campaign.lodgement_date as number, campaign.mail_type);
    if (day) out.push(day);
  }
  return out;
}

/**
 * Why checkout must refuse. Empty means the campaign can be paid for.
 * Artwork and print approval are not checkout gates.
 */
export function checkoutRefusal(input: {
  productId: string | null;
  product: { addressed_mail: number; unaddressed_mail: number } | null;
  mailType: string;
  mailSpeed: string;
  addressCount: number;
  lodgementDate: number | null;
  nowSec: number;
}): string | null {
  if (!input.productId) return "Choose a product first";
  const mailType = input.mailType as MailType;
  if (!allowedSpeeds(mailType).includes(input.mailSpeed as "regular" | "priority")) {
    return "Unaddressed mail is Regular only.";
  }
  if (!input.product || !productAllows(input.product, mailType)) {
    return "That product is not available for this mail type.";
  }
  if (input.addressCount <= 0) return "Campaign has no addresses";
  if (!input.lodgementDate) return "Set a lodgement date";
  const earliest = input.nowSec + LODGEMENT_BUFFER_DAYS * 86400;
  if (input.lodgementDate < earliest) {
    return `Lodgement date must be at least ${LODGEMENT_BUFFER_DAYS} days away`;
  }
  return lodgementDayBlocker(input.lodgementDate, input.mailType);
}

export type CartDecision =
  | { kind: "in_cart" }
  | { kind: "ready" }
  | { kind: "blocked"; message: string };

/**
 * What the Add to Cart button does, and when it cannot, why and what to do.
 * The list payload has no order_status (undefined), so any order blocks there.
 */
export function cartDecision(
  campaign: Parameters<typeof cartBlockers>[0] & {
    status: string;
    order_id: string | null;
    order_status?: string | null;
    in_cart_at?: number | null;
  },
  nowMs = Date.now(),
): CartDecision {
  const duplicate = "Duplicate it to run it again.";
  if (campaign.status === "deleted") {
    return {
      kind: "blocked",
      message: "This campaign has been deleted. Restore it to add it to the cart.",
    };
  }
  if (campaign.status === "cancelled") {
    return {
      kind: "blocked",
      message: "This campaign is cancelled. Reactivate it to add it to the cart.",
    };
  }
  if (campaign.status === "posted") {
    return { kind: "blocked", message: `This campaign has been posted. ${duplicate}` };
  }
  if (customerEditBlocked(campaign.status, campaign.order_status)) {
    return {
      kind: "blocked",
      message: `This campaign is ${orderStatusLabel(campaign.order_status as string)}. ${duplicate}`,
    };
  }
  if (campaign.order_id && campaign.order_status === undefined) {
    return { kind: "blocked", message: `This campaign has already been ordered. ${duplicate}` };
  }
  if (campaign.in_cart_at) return { kind: "in_cart" };
  const reasons = cartBlockers(campaign, nowMs);
  return reasons.length > 0 ? { kind: "blocked", message: reasons.join(" ") } : { kind: "ready" };
}

export type LodgementDecision =
  | { kind: "warn"; message: string }
  | { kind: "blocked"; message: string }
  | { kind: "confirm"; message: string; epoch: number }
  | { kind: "request"; epoch: number; message: string }
  | { kind: "save"; epoch: number };

/** Statuses whose zones stay locked for everyone, staff included. */
const ZONE_LOCKED = new Set(["out_on_proof", "complete", "posted", "cancelled", "rejected"]);

/**
 * The strip under the header. Null means the campaign is open.
 * A paid order locks a customer but not staff; a finished status locks both.
 */
export function lockNotice(
  campaign: { status: string; order_status?: string | null },
  staff: boolean,
): string | null {
  if (ZONE_LOCKED.has(campaign.status)) {
    return `This campaign is ${orderStatusLabel(campaign.status)}. Its zones are locked.`;
  }
  if (!staff && customerEditBlocked(campaign.status, campaign.order_status)) {
    return `This campaign is paid and locked. Zones, addresses, product and mail options cannot be changed. You can request a new lodgement date; eConnect Plus will confirm it. For anything else call ${SUPPORT_PHONE}.`;
  }
  return null;
}

export function lodgementDecision(opts: {
  epoch: number | null;
  formatted: string | null;
  mailType: string;
  campaignStatus: string;
  orderStatus: string | null | undefined;
  staff: boolean;
  nowMs?: number;
}): LodgementDecision {
  if (!lodgementAcceptable(opts.epoch, opts.nowMs)) {
    return { kind: "warn", message: lodgementWarning(opts.formatted) };
  }
  const epoch = opts.epoch as number;
  const day = lodgementDayBlocker(epoch, opts.mailType);
  if (day) return { kind: "warn", message: day };
  if (customerEditBlocked(opts.campaignStatus, opts.orderStatus)) {
    // Posted is finished. A paid campaign can still ask for a new date.
    if (!opts.staff) {
      if (opts.campaignStatus === "posted") {
        return { kind: "blocked", message: CANNOT_CHANGE_MESSAGE };
      }
      return {
        kind: "request",
        epoch,
        message: `This campaign is paid, so eConnect Plus must confirm a new lodgement date. Send a request to move it to ${opts.formatted}?`,
      };
    }
    const label = orderStatusLabel(opts.orderStatus ?? opts.campaignStatus);
    return {
      kind: "confirm",
      epoch,
      message: `This order is ${label}. Change the lodgement date anyway?`,
    };
  }
  return { kind: "save", epoch };
}

export type ApprovalDecision = "approve" | "reject" | "approve_for_print" | "withdraw" | "reopen";

/**
 * The proofing chain. The creator (or staff on their behalf) approves a
 * pending proof; only staff then approve it for print or reject it. The
 * creator can withdraw while it is out on proof. After print approval only
 * staff can reopen it.
 */
export const APPROVAL_TRANSITIONS = {
  approve: {
    from: ["pending"],
    to: "approved_by_creator",
    status: "out_on_proof",
    staffOnly: false,
  },
  reject: {
    from: ["approved_by_creator"],
    to: "rejected",
    status: "in_progress",
    staffOnly: true,
  },
  approve_for_print: {
    from: ["approved_by_creator"],
    to: "approved_for_print",
    status: "complete",
    staffOnly: true,
  },
  withdraw: {
    from: ["approved_by_creator"],
    to: "pending",
    status: "in_progress",
    staffOnly: false,
  },
  reopen: {
    from: ["approved_for_print"],
    to: "pending",
    status: "in_progress",
    staffOnly: true,
  },
} as const;

/**
 * Why a customer cannot change the artwork at this approval state.
 * Null means they can. Staff are never gated here.
 */
/**
 * While the creator's proof is with eConnect, the campaign's setup stays as
 * the proof was built. Lodgement date and mail speed can still move.
 * Staff are gated here too: withdraw or reject the proof first.
 */
export function proofSetupLocked(approvalStatus: string): string | null {
  if (approvalStatus !== "approved_by_creator") return null;
  return "This artwork is out on proof. Only the lodgement date and mail speed can change.";
}

export function approvalEditBlocked(approvalStatus: string, staff: boolean): string | null {
  if (staff) return null;
  if (approvalStatus === "approved_by_creator") {
    return "Your artwork is out on proof with our team. Withdraw your approval to change it.";
  }
  if (approvalStatus === "approved_for_print") {
    return `Your artwork has been approved for print. Send a change request or call eConnect Plus on ${SUPPORT_PHONE}.`;
  }
  return null;
}

/**
 * Columns cleared when the campaign's artwork file is replaced.
 * Pages stay when this request set them. Approval stamps clear unless the
 * campaign was already pending. Out on proof or complete goes back to in
 * progress unless this request set the status itself.
 */
export function artworkChangeColumns(
  campaign: { approval_status: string; status: string },
  opts: { pagesSet: boolean; statusSet: boolean },
): Record<string, string | null> {
  const columns: Record<string, string | null> = { approved_final_pdf_id: null };
  if (!opts.pagesSet) columns.artwork_pages = null;
  if (campaign.approval_status === "pending") return columns;
  columns.approval_status = "pending";
  columns.rejection_reason = null;
  columns.creator_approved_at = null;
  columns.creator_approved_by = null;
  columns.print_approved_at = null;
  columns.print_approved_by = null;
  if (!opts.statusSet && (campaign.status === "out_on_proof" || campaign.status === "complete")) {
    columns.status = "in_progress";
  }
  return columns;
}

/** The approval and campaign status a decision leads to, or null when it is not allowed now. */
export function approvalTransition(
  decision: ApprovalDecision,
  current: string,
  staff: boolean,
): { approval: string; status: string } | null {
  const t = APPROVAL_TRANSITIONS[decision];
  if (t.staffOnly && !staff) return null;
  if (!(t.from as readonly string[]).includes(current)) return null;
  return { approval: t.to, status: t.status };
}

/** Why a customer cannot withdraw. Null means withdraw is allowed. */
export function withdrawRefusal(approvalStatus: string): string | null {
  if (approvalTransition("withdraw", approvalStatus, false)) return null;
  if (approvalStatus === "approved_for_print") return approvalEditBlocked(approvalStatus, false);
  return "There is no approval to withdraw.";
}

export type CancelDecision =
  | { kind: "hidden" }
  | { kind: "blocked"; message: string }
  | { kind: "confirm"; message: string }
  | { kind: "staff-confirm"; message: string };

export function cancelDecision(opts: {
  campaignStatus: string;
  orderStatus: string | null | undefined;
  staff: boolean;
}): CancelDecision {
  if (opts.campaignStatus === "cancelled" || opts.campaignStatus === "posted") {
    return { kind: "hidden" };
  }
  if (customerEditBlocked(opts.campaignStatus, opts.orderStatus)) {
    if (!opts.staff) return { kind: "blocked", message: CANNOT_CANCEL_MESSAGE };
    const label = orderStatusLabel(opts.orderStatus ?? opts.campaignStatus);
    return {
      kind: "staff-confirm",
      message: `This order is ${label}. Cancel the campaign anyway?`,
    };
  }
  return { kind: "confirm", message: "Cancel this campaign?" };
}
